import { useEffect, useState } from "react";
import { Form } from "react-bootstrap";
import type {
	VideoBudgetMode,
	VideoBudgetPeriod,
	VideoPresenterOption,
} from "shared";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { parseError } from "../common/error";
import { Button } from "../components/Button";
import {
	useVideoActions,
	useVideoAvailability,
	useVideoGenerations,
	useVideoOptions,
	useVideoUsage,
} from "../video/useVideoGenerations";

const STATUS_LABEL: Record<string, string> = {
	queued: "Waiting",
	thinking: "In progress",
	reviewing: "In progress",
	generating: "In progress",
	completed: "Ready",
	failed: "Didn't finish",
	cancelled: "Cancelled",
};

function DefaultSelect(props: {
	label: string;
	value: string;
	options: VideoPresenterOption[];
	onChange: (value: string) => void;
	ownedGroupLabel?: string;
}) {
	const owned = props.options.filter((o) => o.owned);
	const stock = props.options.filter((o) => !o.owned);
	return (
		<Form.Group className="mb-2">
			<Form.Label className="small mb-1">{props.label}</Form.Label>
			<Form.Select
				size="sm"
				value={props.value}
				onChange={(e) => props.onChange(e.target.value)}
			>
				<option value="">Let Moxie pick</option>
				{owned.length > 0 && (
					<optgroup label={props.ownedGroupLabel ?? "From your HeyGen"}>
						{owned.map((o) => (
							<option key={o.id} value={o.id}>
								{o.name}
							</option>
						))}
					</optgroup>
				)}
				{stock.length > 0 && (
					<optgroup label={owned.length ? "Stock" : "Options"}>
						{stock.map((o) => (
							<option key={o.id} value={o.id}>
								{o.name}
							</option>
						))}
					</optgroup>
				)}
			</Form.Select>
		</Form.Group>
	);
}

/** Story 5.3 defaults, usage, and the account's own credit budget. */
export const VideoCreationCard = () => {
	const availability = useVideoAvailability();
	const enabled = availability.enabled;
	const options = useVideoOptions(enabled);
	const usage = useVideoUsage(enabled);
	const history = useVideoGenerations(null, enabled, 5);
	const { saveDefaults, saveBudget } = useVideoActions();
	const emit = useAlertEmit();
	const [avatarId, setAvatarId] = useState("");
	const [voiceId, setVoiceId] = useState("");
	const [styleId, setStyleId] = useState("");
	const [budgetMode, setBudgetMode] = useState<VideoBudgetMode>("off");
	const [budgetPeriod, setBudgetPeriod] =
		useState<VideoBudgetPeriod>("monthly");
	const [budgetMinutes, setBudgetMinutes] = useState("60");

	useEffect(() => {
		const defaults = options.data?.defaults;
		if (defaults) {
			setAvatarId(defaults.avatarId ?? "");
			setVoiceId(defaults.voiceId ?? "");
			setStyleId(defaults.styleId ?? "");
		}
	}, [options.data]);

	useEffect(() => {
		const budget = usage.data?.budget;
		if (!budget) {
			return;
		}
		setBudgetMode(budget.mode);
		setBudgetPeriod(budget.period);
		if (budget.minutes != null) {
			setBudgetMinutes(String(budget.minutes));
		}
	}, [usage.data]);

	if (!availability.flagOn) {
		return null;
	}

	return (
		<div className="app-connect">
			<div className="app-connect-header">
				<div className="app-connect-metadata">
					<span className="app-connect-heygen-logo" aria-hidden>
						▶
					</span>
					<div>
						<div>Video creation</div>
						<div>Powered by HeyGen</div>
					</div>
				</div>
			</div>
			<div className="app-connect-spacer" />

			{!enabled ? (
				<p className="text-muted small mb-0">
					{availability.reason ??
						"Connect your HeyGen API key in Settings → Apps to make videos. HeyGen charges your HeyGen credits."}
				</p>
			) : (
				<>
					<p className="text-muted small mb-2">
						Videos use your HeyGen API key. Finished length is billed as HeyGen
						credits on your HeyGen account. Set your own limit below to get a
						warning before you spend more.
					</p>
					{usage.data && (
						<>
							<div className="app-connect-page-selection">
								Made in Moxie this month
								<span className="active">
									{usage.data.minutesUsed} min · {usage.data.videosThisMonth}{" "}
									video{usage.data.videosThisMonth === 1 ? "" : "s"}
								</span>
							</div>
							{usage.data.budget.mode !== "off" &&
								usage.data.budget.minutes != null && (
									<div className="app-connect-page-selection">
										Your {usage.data.budget.period} budget
										<span
											className={
												usage.data.overBudget ? "text-danger" : "active"
											}
										>
											{usage.data.budgetMinutesUsed} of{" "}
											{usage.data.budget.minutes} min
											{usage.data.overBudget ? " · over limit" : ""}
										</span>
									</div>
								)}
						</>
					)}

					<form
						className="mt-2 mb-3"
						onSubmit={(e) => {
							e.preventDefault();
							const minutes = Number(budgetMinutes);
							saveBudget.mutate(
								{
									mode: budgetMode,
									period: budgetPeriod,
									minutes:
										budgetMode === "off"
											? null
											: Number.isFinite(minutes)
												? minutes
												: null,
								},
								{
									onSuccess: () =>
										emit({
											type: AlertType.SUCCESS,
											content: "Video budget saved",
										}),
									onError: (error) =>
										emit({
											type: AlertType.ERROR,
											content:
												parseError(error) ?? "Couldn't save your budget.",
										}),
								},
							);
						}}
					>
						<div className="fw-semibold small mb-2">Credit budget</div>
						<Form.Group className="mb-2">
							<Form.Label className="small mb-1">When you hit it</Form.Label>
							<Form.Select
								size="sm"
								value={budgetMode}
								onChange={(e) =>
									setBudgetMode(e.target.value as VideoBudgetMode)
								}
							>
								<option value="off">No limit</option>
								<option value="warn">Warn me, let me continue</option>
								<option value="block">Block until I raise the limit</option>
							</Form.Select>
						</Form.Group>
						{budgetMode !== "off" && (
							<>
								<Form.Group className="mb-2">
									<Form.Label className="small mb-1">Period</Form.Label>
									<Form.Select
										size="sm"
										value={budgetPeriod}
										onChange={(e) =>
											setBudgetPeriod(e.target.value as VideoBudgetPeriod)
										}
									>
										<option value="monthly">Per calendar month</option>
										<option value="daily">Per day</option>
									</Form.Select>
								</Form.Group>
								<Form.Group className="mb-2">
									<Form.Label className="small mb-1">Minutes</Form.Label>
									<Form.Control
										size="sm"
										type="number"
										min={1}
										max={10000}
										value={budgetMinutes}
										onChange={(e) => setBudgetMinutes(e.target.value)}
									/>
								</Form.Group>
							</>
						)}
						<Button
							type="submit"
							variant="secondary"
							full
							loading={saveBudget.isPending}
						>
							Save budget
						</Button>
					</form>

					{options.data && (
						<form
							className="mt-2"
							onSubmit={(e) => {
								e.preventDefault();
								saveDefaults.mutate(
									{
										avatarId: avatarId || null,
										voiceId: voiceId || null,
										styleId: styleId || null,
									},
									{
										onSuccess: () =>
											emit({
												type: AlertType.SUCCESS,
												content: "Video defaults saved",
											}),
										onError: (error) =>
											emit({
												type: AlertType.ERROR,
												content:
													parseError(error) ?? "Couldn't save your defaults.",
											}),
									},
								);
							}}
						>
							<DefaultSelect
								label="Presenter"
								value={avatarId}
								options={options.data.avatars}
								ownedGroupLabel="Your HeyGen avatars"
								onChange={setAvatarId}
							/>
							<DefaultSelect
								label="Voice"
								value={voiceId}
								options={options.data.voices}
								ownedGroupLabel="Your HeyGen voices"
								onChange={setVoiceId}
							/>
							<DefaultSelect
								label="Style"
								value={styleId}
								options={options.data.styles}
								onChange={setStyleId}
							/>
							<Button
								type="submit"
								variant="secondary"
								full
								loading={saveDefaults.isPending}
							>
								Save defaults
							</Button>
						</form>
					)}
					{options.isError && (
						<p className="text-muted small">
							Presenter choices couldn't load right now.
						</p>
					)}

					{(history.data?.length ?? 0) > 0 && (
						<ul className="list-unstyled small mt-3 mb-0">
							{history.data?.map((video) => (
								<li
									key={video.id}
									className="d-flex justify-content-between gap-2"
								>
									<span className="text-truncate">{video.title}</span>
									<span className="text-muted text-nowrap">
										{STATUS_LABEL[video.status] ?? video.status}
										{video.charged ? ` · ${video.actualSeconds}s` : ""}
									</span>
								</li>
							))}
						</ul>
					)}
				</>
			)}
		</div>
	);
};
